import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import InsightModal from './InsightModal'
import UpgradeModal from './UpgradeModal'
import InsightCard from './InsightCard'
import InsightsUpgradeCard from './InsightsUpgradeCard'
import { canViewDetailedInsights } from '../config/features'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoExecutiveInsights } from '../utils/demoData'
import { TrendingUp, Target, Sparkles, AlertCircle, Lightbulb, BarChart3 } from 'lucide-react'

interface InsightItem {
  title: string
  summary: string
  details: string
}

interface ParsedInsights {
  summary: string
  searchHighlights: InsightItem[]
  marketHighlights: InsightItem[]
  keyRecommendations: InsightItem[]
  fullText: string
}

function parseInsights(insights: any): ParsedInsights | null {
  if (!insights) return null

  // Transform string arrays into InsightItem arrays with title, summary, details
  const transformToInsightItems = (items: string[] | InsightItem[] | undefined): InsightItem[] => {
    if (!items || !Array.isArray(items)) return []

    return items.map((item, index) => {
      // If already in InsightItem format
      if (typeof item === 'object' && item.title) {
        return {
          title: item.title,
          summary: item.summary || item.title,
          details: item.details || item.summary || item.title
        }
      }
      // If it's a string, create an InsightItem from it
      const text = typeof item === 'string' ? item : String(item)
      // Try to extract a title from the first part of the text
      const parts = text.split(/[:.!?]/)
      const title = parts[0]?.trim() || `Insight ${index + 1}`
      return {
        title: title.length > 60 ? title.substring(0, 60) + '...' : title,
        summary: text.length > 150 ? text.substring(0, 150) + '...' : text,
        details: text
      }
    })
  }

  return {
    summary: insights.summary || '',
    searchHighlights: transformToInsightItems(insights.searchHighlights),
    marketHighlights: transformToInsightItems(insights.marketHighlights),
    keyRecommendations: transformToInsightItems(insights.keyRecommendations),
    fullText: JSON.stringify(insights, null, 2)
  }
}

export default function ExecutiveSummaryInsights() {
  const { activeBrand, currentUser } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [parsedInsights, setParsedInsights] = useState<ParsedInsights | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedInsights, setExpandedInsights] = useState(false)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedInsight, setSelectedInsight] = useState<InsightItem | null>(null)
  const [modalIcon, setModalIcon] = useState<React.ReactNode>(null)
  const [modalIconColor, setModalIconColor] = useState('text-indigo-600')
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // Check if user has access to AI Insights (PRO only)
  const hasInsightsAccess = activeBrand?.subscriptionProduct === 'PRO'

  const openModal = (insight: InsightItem, icon: React.ReactNode, iconColor: string) => {
    const subscriptionTier = currentUser?.subscriptionTier
    const access = canViewDetailedInsights(subscriptionTier)

    if (access.allowed) {
      setSelectedInsight(insight)
      setModalIcon(icon)
      setModalIconColor(iconColor)
      setModalOpen(true)
    } else {
      setShowUpgradeModal(true)
    }
  }

  useEffect(() => {
    const brandId = activeBrand?.brandId || activeBrand?.id
    if (brandId && hasInsightsAccess) {
      fetchAllInsights()
    } else {
      setLoading(false)
    }
  }, [activeBrand?.brandId, activeBrand?.id, hasInsightsAccess])

  const fetchAllInsights = async () => {
    const brandId = activeBrand?.brandId || activeBrand?.id
    if (!brandId) return

    try {
      setLoading(true)
      setError(null)

      // Use demo data in demo mode
      if (isDemoMode) {
        setParsedInsights(parseInsights(demoExecutiveInsights))
        setLoading(false)
        return
      }

      const response = await api.get(`/brand-analysis/${brandId}/ai-insights?context=executive`)

      if (response.data.success) {
        setParsedInsights(parseInsights(response.data.insights))
      }
    } catch (error: any) {
      console.error('Failed to fetch executive insights:', error)
      if (error.response?.status === 404) {
        setError('pending')
      } else {
        setError('Unable to load brand insights')
      }
    } finally {
      setLoading(false)
    }
  }

  // Upgrade prompt for STARTER/TRIAL users
  if (!hasInsightsAccess) {
    return (
      <InsightsUpgradeCard
        title="AI Insights & Recommendations"
        description="Unlock personalized AI marketing strategies and competitive insights"
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Insights Being Generated State */}
      {!loading && !parsedInsights && error === 'pending' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h5 className="font-semibold text-blue-900 mb-1">Insights Being Generated</h5>
              <p className="text-sm text-blue-800">
                Your personalized brand insights are being generated. This happens automatically during onboarding. Check back soon!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {!loading && error && error !== 'pending' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold text-red-900 mb-1">Unable to Load Insights</h5>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Brand Insights Overview */}
      {!loading && parsedInsights && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow border border-indigo-100 p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-900">Brand Insights & Recommendations</h3>
            </div>
          </div>

          <div className={`overflow-hidden transition-all ${expandedInsights ? 'max-h-none' : 'max-h-48'}`}>
            {/* Executive Summary */}
            {parsedInsights.summary && (
              <div className="mb-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {parsedInsights.summary}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Search Intelligence Highlights */}
              {parsedInsights.searchHighlights.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-blue-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Search Intelligence</h4>
                  </div>
                  <div className="space-y-2">
                    {parsedInsights.searchHighlights.map((item, index) => (
                      <InsightCard
                        key={index}
                        index={index}
                        title={item.title}
                        summary={item.summary}
                        accentColor="text-blue-600"
                        borderColor="border-blue-300"
                        bgColor="bg-blue-50"
                        onClick={() => openModal(item, <Lightbulb className="w-5 h-5" />, 'text-blue-600')}
                        showBadge
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Market Intelligence Highlights */}
              {parsedInsights.marketHighlights.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Market Intelligence</h4>
                  </div>
                  <div className="space-y-2">
                    {parsedInsights.marketHighlights.map((item, index) => (
                      <InsightCard
                        key={index}
                        index={index}
                        title={item.title}
                        summary={item.summary}
                        accentColor="text-green-600"
                        borderColor="border-green-300"
                        bgColor="bg-green-50"
                        onClick={() => openModal(item, <BarChart3 className="w-5 h-5" />, 'text-green-600')}
                        showBadge
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Key Recommendations / Priority Actions */}
            {parsedInsights.keyRecommendations.length > 0 && (
              <div className="mt-6 bg-white rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-orange-600" />
                  <h4 className="font-semibold text-gray-900 text-sm">Priority Actions</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {parsedInsights.keyRecommendations.map((rec, index) => (
                    <InsightCard
                      key={index}
                      index={index}
                      title={rec.title}
                      summary={rec.summary}
                      accentColor="text-orange-600"
                      borderColor="border-orange-300"
                      bgColor="bg-orange-50"
                      onClick={() => openModal(rec, <Target className="w-5 h-5" />, 'text-orange-600')}
                      showBadge
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Expand/Collapse Button */}
          {!expandedInsights && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <button
                onClick={() => setExpandedInsights(true)}
                className="bg-white text-indigo-600 hover:text-indigo-700 px-4 py-2 rounded-2xl shadow font-medium text-sm border border-indigo-200 hover:shadow-md transition-all"
              >
                Expand
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow border border-indigo-100 p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative flex items-center justify-center w-16 h-16">
              <div className="absolute inset-0 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
              <Sparkles className="w-8 h-8 text-indigo-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Loading brand insights</h3>
              <p className="text-sm text-gray-600">This may take a few moments...</p>
            </div>
          </div>
        </div>
      )}

      {/* Insight Detail Modal */}
      {selectedInsight && (
        <InsightModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={selectedInsight.title}
          summary={selectedInsight.summary}
          details={selectedInsight.details}
          icon={modalIcon}
          iconColor={modalIconColor}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="detailed brand insights"
      />
    </div>
  )
}
